/**
 * Bulk note-count fetcher for the auto-injected notes column.
 *
 * Issues one ``POST /admin/exception-notes/counts`` per visible
 * page of rows. Composite key shape ``${exception_code}|${data_domain}
 * |${object_id}`` so future cross-type / "All Exceptions" views can't
 * collide on bare object_id. When a row carries its own ``fp_code``
 * (platform-report tables span FPs), the composite key includes it -
 * the inspector path keeps the bare three-part shape because the
 * caller already supplied the table-level ``fpCode`` argument.
 *
 * RBAC: FP users short-circuit to an empty map; the server is still
 * the source of truth.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../services/api'
import { canSeeExceptionNotes } from '../services/auth'

export interface CountKeyParts {
  exception_code: string
  data_domain: string
  object_id: string
  /** Per-row fp_code, used when the visible page spans multiple FPs
   * (platform-report tables). Omit on inspector calls - the caller
   * passes a table-level ``fpCode`` argument instead. */
  fp_code?: string
}

interface CountEntry extends CountKeyParts {
  count: number
  /** Notes on the same object that live on a different exception type. */
  sibling_count?: number
}

interface CountsResponse {
  counts: CountEntry[]
}

export const compositeKey = (parts: CountKeyParts): string =>
  parts.fp_code
    ? `${parts.fp_code}|${parts.exception_code}|${parts.data_domain}|${parts.object_id}`
    : `${parts.exception_code}|${parts.data_domain}|${parts.object_id}`

export interface UseExceptionNoteCountsResult {
  counts: Record<string, number>
  /** Per-row count of notes on the same object but a different exception
   * type. Drives the "related exception" outline dot. */
  siblingCounts: Record<string, number>
  loading: boolean
  /** True once a fetch for the CURRENT row set has finished (success, error
   * or short-circuit). ``loading`` starts false and only flips true after the
   * effect fires, so a naive ``!loading`` gate races on row-set change - this
   * is the gate-safe signal for the drawer's render-all-at-once skeleton. */
  settled: boolean
  invalidate: (parts: CountKeyParts, delta?: number) => void
  refresh: () => Promise<void>
}

/**
 * Pass the visible-page rows; the hook batches a single counts call
 * per change of the row set. The ``invalidate`` callback lets the
 * notes hook bump a single row's count without re-fetching the
 * whole page (handy after a create / delete).
 *
 * ``fpCode`` is the table-level franchise (inspector). For platform
 * tables that span FPs, pass ``undefined`` and set ``fp_code`` on each
 * row in ``rows``.
 */
export function useExceptionNoteCounts(
  fpCode: string | undefined,
  rows: CountKeyParts[],
): UseExceptionNoteCountsResult {
  const { user } = useAuth()
  const gated = canSeeExceptionNotes(user)
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [siblingCounts, setSiblingCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  // The rowHash whose fetch last completed - settled in ``finally`` (and on
  // every short-circuit) so a failed call never freezes a loading gate.
  const [settledHash, setSettledHash] = useState<string | null>(null)

  // Stable hash so a parent re-render that builds a new array with
  // identical contents doesn't refetch.
  const rowHash = useMemo(() => {
    if (!rows.length) return ''
    return rows.map(compositeKey).sort().join('||')
  }, [rows])

  const refresh = useCallback(async (): Promise<void> => {
    if (!gated || rows.length === 0) {
      setCounts({})
      setSiblingCounts({})
      setSettledHash(rowHash)
      return
    }
    // Either the caller passed a table-level fp_code OR every row
    // carries its own fp_code. Otherwise the request can't resolve
    // any tuple, so short-circuit to the empty map.
    const allRowsHaveFp = rows.every(r => !!r.fp_code)
    if (!fpCode && !allRowsHaveFp) {
      setCounts({})
      setSiblingCounts({})
      setSettledHash(rowHash)
      return
    }
    setLoading(true)
    try {
      const res = await apiClient.post<CountsResponse>(
        '/admin/exception-notes/counts',
        { fp_code: fpCode, rows },
      )
      const next: Record<string, number> = {}
      const nextSiblings: Record<string, number> = {}
      for (const entry of res.data.counts) {
        const k = compositeKey(entry)
        next[k] = entry.count
        nextSiblings[k] = entry.sibling_count ?? 0
      }
      setCounts(next)
      setSiblingCounts(nextSiblings)
    } catch {
      // Swallow - the column is a soft surface, not a blocker.
      setCounts({})
      setSiblingCounts({})
    } finally {
      setLoading(false)
      setSettledHash(rowHash)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated, fpCode, rowHash])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const invalidate = useCallback(
    (parts: CountKeyParts, delta: number = 0): void => {
      const k = compositeKey(parts)
      setCounts((prev) => {
        const current = prev[k] ?? 0
        const next = Math.max(0, current + delta)
        return { ...prev, [k]: next }
      })
    },
    [],
  )

  return {
    counts,
    siblingCounts,
    loading,
    settled: settledHash === rowHash,
    invalidate,
    refresh,
  }
}
